import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader, SectionHeader } from '@/components/ui/page-header'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { BusinessPortal } from '@/components/sales/BusinessPortal'
import { BUSINESS_STATUS, SET_UP_HINT } from '@/components/sales/VenueBusinessSection'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useCreateSalesBusiness, useSalesBusinesses } from '@/hooks/useSales'
import { useOrganization } from '@/hooks/useOrganization'

/**
 * How the company gets paid for its venues' sales (docs/sales.spec.md,
 * "Stripe"): its business, verified once inside the app, and then its
 * payments, payouts and reports. A venue that's a business of its own is paid
 * separately, from its settings.
 */
export function GettingPaidPage() {
  const organization = useOrganization()
  const query = useSalesBusinesses()
  const create = useCreateSalesBusiness()

  if (query.isPending) {
    return (
      <PageContainer width="wide">
        <DetailSkeleton />
      </PageContainer>
    )
  }
  if (query.isError) {
    return (
      <PageContainer width="wide">
        <ErrorState message={requestErrorMessage(query.error)} onRetry={() => void query.refetch()} />
      </PageContainer>
    )
  }
  const data = query.data
  const company = data.company
  const separate = data.venues.filter((v) => v.business !== null)

  return (
    <PageContainer width="wide" className="space-y-8">
      <PageHeader title="Getting paid" meta={organization.data?.name} />
      {!data.available ? (
        <p className="m-0 text-sm text-ink-muted">Taking payments isn’t available yet.</p>
      ) : company === null ? (
        <Callout
          tone="neutral"
          title="Set up the business that gets paid"
          action={
            <Button onClick={() => create.mutate(null)} disabled={create.isPending}>
              {create.isPending ? 'Starting…' : 'Set it up'}
            </Button>
          }
        >
          {SET_UP_HINT} Couples pay it directly, its name is on their statements, and payments land in its bank account. Every venue is paid
          through it unless a venue is set up on its own.
          {create.isError ? <span className="mt-2 block text-danger-ink">{requestErrorMessage(create.error)}</span> : null}
        </Callout>
      ) : (
        <section className="space-y-4">
          <p className="m-0 flex flex-wrap items-center gap-2 text-sm text-ink">
            The company’s business
            <Badge tone={BUSINESS_STATUS[company.status].tone}>{BUSINESS_STATUS[company.status].label}</Badge>
          </p>
          <BusinessPortal business={company} />
        </section>
      )}

      {separate.length === 0 ? null : (
        <section className="space-y-3">
          <SectionHeader title="Venues paid separately" />
          <Table>
            <TableHeader>
              <TableHead>Venue</TableHead>
              <TableHead>Its business</TableHead>
            </TableHeader>
            <TableBody>
              {separate.map((v) => (
                <TableRow key={v.venueId}>
                  <TableCell>
                    <Link to={`/venues/${v.venueId}/settings#getting-paid`} className="font-medium text-ink underline-offset-2 hover:underline">
                      {v.venueName}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {v.business === null ? null : <Badge tone={BUSINESS_STATUS[v.business.status].tone}>{BUSINESS_STATUS[v.business.status].label}</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      )}
    </PageContainer>
  )
}
